import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import BorderStyleDashed from '@/assets/icons/cg_border_style_dashed.svg?component';
import AlertLine from '@/assets/icons/ri_alert_line.svg?component';
import CheckboxCircleLine from '@/assets/icons/ri_checkbox_circle_line.svg?component';
import { type IconComponent, SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip, { TooltipRows } from '@/components/atoms/Tooltip';
import { alertIconColors, settings } from '@/config/settings';
import type { OverviewCage } from '@/domain/client';
import {
    calculateFishSpeedToolTip,
    getSpeedStatus,
} from '@/utils/SharedDataCalculation/swimSpeedCalculations';
import { createCageUrl } from '@/utils/urlGenerator';

function getIcon(swimSpeed?: number): [string, IconComponent] {
    if (swimSpeed == null) {
        return [alertIconColors.neutral, BorderStyleDashed];
    }
    if (swimSpeed > settings.swimspeed.high_speed_threshold) {
        return [alertIconColors.danger, AlertLine];
    }
    if (
        swimSpeed < settings.swimspeed.low_speed_threshold ||
        swimSpeed > settings.swimspeed.medium_speed_threshold
    ) {
        return [alertIconColors.warning, AlertLine];
    }
    return [alertIconColors.success, CheckboxCircleLine];
}

interface SwimSpeedIconLinkProps {
    href: string;
    icon: IconComponent;
    color: string;
    /** The link shows only an icon, so this is all a screen reader has to go on. */
    label: string;
}

function SwimSpeedIconLink(props: SwimSpeedIconLinkProps) {
    const { href, icon, color, label } = props;

    return (
        <Link to={href} aria-label={label}>
            <SvgIcon
                icon={icon}
                size="custom"
                customSize="1.7em"
                color={color}
                svgProps={{ 'aria-hidden': true }}
            />
        </Link>
    );
}

export interface LocationOverviewSwimSpeedCellProps {
    cage: OverviewCage;
    clientId: number;
    locationId: number;
    isBehaviorModuleEnabled: boolean;
}

export default function LocationOverviewSwimSpeedCell(props: LocationOverviewSwimSpeedCellProps) {
    const { cage, clientId, locationId, isBehaviorModuleEnabled } = props;
    const { t } = useTranslation();

    const swimSpeed = cage.cageData.swimSpeed;
    const cageUrl = createCageUrl(clientId, locationId, cage.cageId);
    const href = isBehaviorModuleEnabled
        ? `${cageUrl}/behavior/swim-speed`
        : `${cageUrl}/welfare/speed`;

    const status = getSpeedStatus(swimSpeed?.swimSpeed, t);
    const tooltipRows = calculateFishSpeedToolTip(status, { swimSpeed }, t);
    const [color, Icon] = getIcon(swimSpeed?.swimSpeed);

    // The tooltip is portalled into `document.body`, so it neither contributes to the overflow
    // of the table's scroll container nor gets clipped by it, and Popper flips the bubble
    // itself when there is no room above the row.
    return (
        <Tooltip content={<TooltipRows rows={tooltipRows} />} layout="fit">
            <SwimSpeedIconLink
                href={href}
                icon={Icon}
                color={color}
                label={`${t('common.labels.swimSpeed')}: ${status}`}
            />
        </Tooltip>
    );
}
